import { type PropsWithChildren, useEffect, useState } from 'react';
import {
   LDProvider,
   type LDContext,
} from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { getDistinctId } from 'src/analytics/mixpanel';
import { useLDFlagSync } from './useLDFlagSync';

const ANON_KEY_STORAGE = 'npt_anon_id';

/**
 * Stable anonymous bucketing key for LaunchDarkly.
 *
 * We deliberately avoid using Mixpanel's distinct_id as the LD bucket key
 * because Mixpanel's identity_merge can rotate the distinct_id when an
 * anonymous session is merged into an identified user — which would
 * re-bucket the user mid-experiment and destroy variant stickiness.
 *
 * Instead, we store a UUID in localStorage the first time the user loads
 * the app and reuse it forever (or until they clear storage). Mixpanel's
 * distinct_id is sent as a separate custom attribute on the LD context so
 * targeting rules can still reference it if needed.
 */
const getOrCreateAnonKey = (): string => {
   try {
      const existing = localStorage.getItem(ANON_KEY_STORAGE);
      if (existing) return existing;
      const fresh = uuidv4();
      localStorage.setItem(ANON_KEY_STORAGE, fresh);
      return fresh;
   } catch {
      // Private-mode or storage disabled — return a per-load UUID. The
      // user will re-bucket on every page load, but experiments still work.
      return uuidv4();
   }
};

interface BuildAnonContextOptions {
   organizationSlug?: string;
}

const buildAnonymousContext = ({ organizationSlug }: BuildAnonContextOptions): LDContext => ({
   kind: 'user',
   key: getOrCreateAnonKey(),
   anonymous: true,
   // Extra attributes — LD targeting rules can segment on these without
   // changing the bucket key.
   mixpanelDistinctId: getDistinctId() ?? undefined,
   organizationSlug: organizationSlug ?? undefined,
});

/**
 * Wraps the app in LaunchDarkly's provider. Must mount INSIDE the
 * Mixpanel provider (so `getDistinctId()` can resolve) and OUTSIDE the
 * AuthProvider (so AuthContext can call `ldClient.identify()` on login).
 *
 * When `VITE_LD_CLIENT_ID` is not set (common in local dev), we render
 * children without LD at all. Every `useExperiment` call will fall back
 * to defaults — a non-breaking no-op.
 */
const LDFlagSync: React.FC = () => {
   useLDFlagSync();
   return null;
};

export const ExperimentProvider: React.FC<PropsWithChildren> = ({ children }) => {
   const clientSideID = import.meta.env.VITE_LD_CLIENT_ID as string | undefined;
   const params = useParams();

   // Lock in the initial context synchronously so LD has something to
   // evaluate against before first paint. Later context updates (login,
   // org change) go through `ldClient.identify()` — not prop changes —
   // to avoid re-mounting the tree.
   const [initialContext] = useState<LDContext>(() =>
      buildAnonymousContext({ organizationSlug: params.organizationSlug }),
   );

   // Update the LD context when the user navigates between orgs while
   // still anonymous. `identify()` is a no-op when LD is missing because
   // this effect only runs with LDProvider mounted below.
   useEffect(() => {
      // The ldClient isn't reachable from here (it lives inside LDProvider).
      // Org-slug updates after login are handled by AuthContext instead.
   }, [params.organizationSlug]);

   if (!clientSideID) {
      return <>{children}</>;
   }

   return (
      <LDProvider
         clientSideID={clientSideID}
         context={initialContext}
         options={{
            // Reduce noise in the console — the SDK logs every streaming
            // event at info level by default.
            logger: {
               debug: () => undefined,
               info: () => undefined,
               warn: (msg: string) => console.warn('[LD]', msg),
               error: (msg: string) => console.error('[LD]', msg),
            },
            // Stream flag changes in real time so kill-switch flips
            // propagate within seconds. Disable in local dev if it's
            // chatty — the default is already `true`.
            streaming: true,
         }}
         reactOptions={{
            // Match our kebab-case flag key convention; the hook's
            // `variation()` call bypasses this anyway.
            useCamelCaseFlagKeys: true,
         }}
      >
         <LDFlagSync />
         {children}
      </LDProvider>
   );
};
